// ═══════════════════════════════════════════════════════════════════════
// interfaceTaxonomy.js — 107-flow CLM interface taxonomy
// ═══════════════════════════════════════════════════════════════════════
// Source: CLM_Interface_Taxonomy.csv  (9 L1 categories · 107 specific flows)
//
// Schema: each flow is the cross-section of what must happen at a
// system boundary. Every flow carries enough metadata to be the basis
// of an interface spec: purpose, type, payload shape, source/destination,
// required infrastructure, severity, fallback.
//
// L1 categories: Material · Energy · Signal · Data · Control · Safety ·
//                Human · Security · Organizational
//
// Severity scale (1-5):
//   5 — Production cannot continue safely; immediate stop conditions
//   4 — Production continues at significant cost/risk; obligations soon
//   3 — Productivity loss; manageable for weeks
//   2 — Inconvenience; manual workaround is sustainable
//   1 — Cosmetic / reporting impact
// ═══════════════════════════════════════════════════════════════════════

export const TAXONOMY_VERSION = "1.0";
export const TAXONOMY_SOURCE  = "CLM_Interface_Taxonomy.csv";

// ─── L1 category metadata ─────────────────────────────────────────────
export const L1_CATEGORIES = [
  { id: "Material",       icon: "📦", color: "#fb923c", desc: "Anything physical that must move - solids, liquids, gases, powders, parts, WIP, finished, waste" },
  { id: "Energy",         icon: "⚡", color: "#ffd600", desc: "Electrical, mechanical, thermal, EM/optical, acoustic, stored" },
  { id: "Signal",         icon: "📡", color: "#38a8ff", desc: "Analog and digital electrical primitives below the data layer" },
  { id: "Data",           icon: "💾", color: "#00e5ff", desc: "Structured information - telemetry, events, command, master, transactional, engineering, quality, media, semantic" },
  { id: "Control",        icon: "🎯", color: "#00ff88", desc: "Closed-loop control flows - inner / mid / outer / outermost loop + adaptive" },
  { id: "Safety",         icon: "🛡️", color: "#ff4444", desc: "Functional safety - hardwired chain, safety bus, drive safety state" },
  { id: "Human",          icon: "👤", color: "#c084fc", desc: "Operator input and output - tactile, voice, scan, biometric, visual, audible, haptic" },
  { id: "Security",       icon: "🔐", color: "#ff4f8b", desc: "Credentials, crypto, audit - the trust fabric" },
  { id: "Organizational", icon: "🏢", color: "#00d4c0", desc: "B2B, internal workflow, regulatory reporting, customer feedback" },
];

// ─── Severity metadata ────────────────────────────────────────────────
export const SEVERITY_META = {
  5: { label: "Critical",  color: "#ff4444", description: "Production cannot continue safely; immediate stop conditions" },
  4: { label: "High",      color: "#fb923c", description: "Production continues at significant cost/risk; obligations breached soon" },
  3: { label: "Medium",    color: "#ffd600", description: "Productivity loss and slower decision-making; manageable for weeks" },
  2: { label: "Low",       color: "#38a8ff", description: "Inconvenience; manual workaround sustainable" },
  1: { label: "Minimal",   color: "#64b5d6", description: "Cosmetic / reporting impact" },
};

// ─── Interface type families (derived from the Interface Required column)
// Each flow's `interfaceType` is one of these — coarse-grained grouping
// of HOW the flow is physically/logically carried.
export const INTERFACE_TYPES = {
  "material-handling":   { label: "Material handling",      color: "#fb923c", icon: "🚚" },
  "pneumatic":           { label: "Pneumatic / fluid",      color: "#38a8ff", icon: "💨" },
  "piping":              { label: "Piping / hydraulic",     color: "#00d4c0", icon: "🚰" },
  "electrical-power":    { label: "Electrical power",       color: "#ffd600", icon: "⚡" },
  "mechanical":          { label: "Mechanical coupling",    color: "#c084fc", icon: "⚙️" },
  "thermal":             { label: "Thermal coupling",       color: "#ff4f8b", icon: "🔥" },
  "optical":             { label: "Optical / EM",           color: "#00ff88", icon: "💡" },
  "discrete-io":         { label: "Discrete I/O wiring",    color: "#64b5d6", icon: "🔌" },
  "analog-io":           { label: "Analog I/O wiring",      color: "#38a8ff", icon: "📈" },
  "fieldbus":            { label: "Industrial fieldbus",    color: "#00e5ff", icon: "🛰️" },
  "industrial-ethernet": { label: "Industrial Ethernet",    color: "#00ff88", icon: "🌐" },
  "vision":              { label: "Machine vision link",    color: "#c084fc", icon: "📷" },
  "wireless":            { label: "Wireless / radio",       color: "#fbbf24", icon: "📶" },
  "file-exchange":       { label: "File exchange",          color: "#64b5d6", icon: "📁" },
  "rest-api":            { label: "REST / HTTP API",        color: "#00d4c0", icon: "🔗" },
  "messaging":           { label: "Messaging / MQ",         color: "#00ff88", icon: "📬" },
  "b2b-gateway":         { label: "B2B gateway (EDI)",      color: "#ffb300", icon: "🤝" },
  "database":            { label: "Database access",        color: "#a78bfa", icon: "🗄️" },
  "safety-circuit":      { label: "Safety circuit",         color: "#ff4444", icon: "🛑" },
  "safety-bus":          { label: "Safety bus",             color: "#ff4444", icon: "🚨" },
  "scanner-hid":         { label: "Scanner / HID",          color: "#38a8ff", icon: "🔍" },
  "audio":               { label: "Audio I/F",              color: "#c084fc", icon: "🔊" },
  "pki":                 { label: "PKI / certificate",      color: "#ff4f8b", icon: "🔑" },
  "iam":                 { label: "IAM / identity",         color: "#ff4f8b", icon: "👤" },
};

// ─── Payload type classification ──────────────────────────────────────
// Independent of category — what does the interface actually carry?
export const PAYLOAD_TYPES = {
  "physical":     { label: "Physical material",        color: "#fb923c" },
  "fluid":        { label: "Fluid (liquid/gas)",       color: "#38a8ff" },
  "energy":       { label: "Energy (raw)",             color: "#ffd600" },
  "electrical":   { label: "Electrical primitive",     color: "#00e5ff" },
  "scalar":       { label: "Scalar value",             color: "#38a8ff" },
  "vector":       { label: "Vector / waveform",        color: "#c084fc" },
  "structured":   { label: "Structured record",        color: "#00d4c0" },
  "document":     { label: "Document / file",          color: "#64b5d6" },
  "media-image":  { label: "Image",                    color: "#a78bfa" },
  "media-video":  { label: "Video stream",             color: "#ff4f8b" },
  "media-audio":  { label: "Audio",                    color: "#fbbf24" },
  "command":      { label: "Command",                  color: "#ff4444" },
  "event":        { label: "Event",                    color: "#00ff88" },
  "geometry-3d":  { label: "3D geometry",              color: "#a78bfa" },
  "geometry-2d":  { label: "2D drawing",               color: "#64b5d6" },
  "model":        { label: "ML/simulation model",      color: "#00ff88" },
  "credential":   { label: "Credential / token",       color: "#ff4f8b" },
  "key-material": { label: "Cryptographic key",        color: "#ff4f8b" },
  "signature":    { label: "Digital signature",        color: "#ff4f8b" },
  "transaction":  { label: "Business transaction",     color: "#00d4c0" },
  "boolean":      { label: "Discrete state",           color: "#64b5d6" },
};

// ─── L2 sub-categories per L1 ─────────────────────────────────────────
export const L2_SUBCATEGORIES = {
  Material:       ["Raw Material", "Work-in-Process", "Finished Goods", "Reverse / Disposal", "Environmental Inputs"],
  Energy:         ["Electrical", "Mechanical", "Thermal", "EM / Optical", "EM", "Acoustic", "Stored"],
  Signal:         ["Analog", "Digital"],
  Data:           ["Telemetry", "Event", "Configuration", "Master", "Command", "Transactional",
                   "Engineering", "Quality", "Identification", "Media - Image", "Media - Video",
                   "Media - Audio", "Media - Document", "Semantic"],
  Control:        ["Inner Loop", "Mid Loop", "Outer Loop", "Outermost Loop", "Adaptive", "Closed-loop CAD<->CMM"],
  Safety:         ["Functional"],
  Human:          ["Input", "Output", "Identity"],
  Security:       ["Credential", "Crypto", "Audit"],
  Organizational: ["B2B", "Internal", "Regulatory", "Customer"],
};

// ─── The 107 flows ────────────────────────────────────────────────────
//
// Each row has 14 keys:
//   id          stable numeric ID (1–107) from the CSV
//   l1          category
//   l2          sub-category
//   l3          specific flow name
//   examples    real-world instances
//   source      typical origin
//   destination typical landing point
//   required    interface(s) required
//   stops       first-order consequence if absent
//   workaround  manual / degraded fallback
//   severity    1–5
//   interfaceType — keyed into INTERFACE_TYPES
//   payloadType   — keyed into PAYLOAD_TYPES
//   protocol      — typical protocol/format (free text)

export const FLOWS = [
  // ─── Material · Raw Material ───────────────────────────────────────
  { id: 1, l1: "Material", l2: "Raw Material", l3: "Bulk solids",
    examples: "Metal billets, coils, plates, sheet stock",
    source: "Warehouse, supplier dock", destination: "Machining / forming cells",
    required: "Material-handling (AGV, conveyor, gantry, forklift)",
    stops: "Cells starve; lines stop; raw stock piles at dock",
    workaround: "Manual transport by forklift / hand cart", severity: 5,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "Physical · AGV · conveyor · forklift" },
  { id: 2, l1: "Material", l2: "Raw Material", l3: "Bulk granular / powder",
    examples: "Plastic pellets, metal/ceramic powders (AM), pigments",
    source: "Silos, sealed cassettes", destination: "IMMs, extruders, AM build chambers",
    required: "Pneumatic conveying, sieving stations, sealed dock",
    stops: "Process feed interrupted; powder contamination risk; AM build aborts",
    workaround: "Manual scoop & weigh; sealed-container handover", severity: 4,
    interfaceType: "pneumatic", payloadType: "physical",
    protocol: "Pneumatic conveying · sealed cassette" },
  { id: 3, l1: "Material", l2: "Raw Material", l3: "Liquids - process",
    examples: "Coolant, cutting fluid, hydraulic oil, lubricants",
    source: "Central plant, totes, HPU", destination: "Spindles, presses, hydraulics",
    required: "Piping, quick-disconnects, manifolds",
    stops: "Spindle overheats; press loses force; tool life collapses",
    workaround: "Hand-fill reservoirs; gravity feed", severity: 5,
    interfaceType: "piping", payloadType: "fluid",
    protocol: "Piping · QD couplings · manifold" },
  { id: 4, l1: "Material", l2: "Raw Material", l3: "Liquids - chemicals",
    examples: "Cleaning agents, plating baths, etchants, paints",
    source: "Drums, bulk tanks", destination: "Process tanks, sprayers, applicators",
    required: "Dosing pumps, mass-flow control, chemical lines",
    stops: "Inconsistent dosing -> off-spec; safety hazard with hand-pour",
    workaround: "Manual metered pour with PPE", severity: 4,
    interfaceType: "piping", payloadType: "fluid",
    protocol: "Dosing pumps · MFC · chemical lines" },
  { id: 5, l1: "Material", l2: "Raw Material", l3: "Gases - utility",
    examples: "Compressed air, nitrogen, instrument air",
    source: "Compressor room, ring main, bulk tank", destination: "Cylinders, grippers, blowoff",
    required: "Pneumatic fittings, regulators, FRL units",
    stops: "Grippers don't actuate; clamps release; line stops",
    workaround: "Bottled gas at point of use", severity: 5,
    interfaceType: "pneumatic", payloadType: "fluid",
    protocol: "Pneumatic ring main · FRL" },
  { id: 6, l1: "Material", l2: "Raw Material", l3: "Gases - inert / specialty",
    examples: "Argon, helium, hydrogen, etch gases, O2",
    source: "Bulk tank, gas yard, dewar", destination: "Welders, AM, deposition, etchers",
    required: "Gas manifolds, MFCs, double-block & bleed, leak detection",
    stops: "Welds porous; AM builds fail; semi processes stop; safety risk",
    workaround: "Bottled gas with manual regulator; reduced flow rate", severity: 5,
    interfaceType: "pneumatic", payloadType: "fluid",
    protocol: "MFC · DBB · leak detection" },
  { id: 7, l1: "Material", l2: "Raw Material", l3: "Consumables / tools",
    examples: "Cutting tools, inserts, fasteners, abrasives, wires",
    source: "Tool crib, vendor", destination: "Spindles, robot EOAT, welders, assembly",
    required: "Tool changers (HSK/BT/CAT), presetters, RFID tool ID",
    stops: "Tool change becomes manual; wrong tool risk; cycle time x3-5",
    workaround: "Operator hand-loads, hand-presets, paper traveler", severity: 4,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "HSK/BT/CAT taper · RFID tool ID · presetter" },

  // ─── Material · WIP ────────────────────────────────────────────────
  { id: 8, l1: "Material", l2: "Work-in-Process", l3: "Parts between cells",
    examples: "Machined parts, sub-assemblies, kits, totes",
    source: "Upstream cell, buffer", destination: "Downstream cell, kitting station",
    required: "Conveyor, robot pick/place, AGV/AMR, kit cart",
    stops: "Cells decouple; WIP piles up; no flow balancing",
    workaround: "Manual transport with paper traveler", severity: 4,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "Conveyor · AGV/AMR · robot pick-place" },
  { id: 9, l1: "Material", l2: "Work-in-Process", l3: "Returned / rework",
    examples: "Defective parts pulled for rework, scrap segregation",
    source: "Inspection station", destination: "Rework cell, scrap bin",
    required: "Diverter conveyor, robot, RMA workflow",
    stops: "Defects re-enter line; scrap mingles with good stock",
    workaround: "Operator hand-sort with kanban bins", severity: 4,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "Diverter conveyor · disposition workflow" },

  // ─── Material · Finished Goods ─────────────────────────────────────
  { id: 10, l1: "Material", l2: "Finished Goods", l3: "Packaged units",
    examples: "Cartons, cases, pallets, individual units",
    source: "Final / packaging cell", destination: "Warehouse, dispatch, customer",
    required: "Palletizer, AS/RS, AGV, label-and-apply",
    stops: "Ship-out backs up; dock congestion; damaged units",
    workaround: "Manual palletize and stage", severity: 4,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "Palletizer · AS/RS · print-and-apply" },

  // ─── Material · Reverse / Disposal ─────────────────────────────────
  { id: 11, l1: "Material", l2: "Reverse / Disposal", l3: "Returns inbound",
    examples: "RMAs, warranty returns, recall items",
    source: "Customer, field", destination: "Returns center, rework",
    required: "Returns workflow, scanners, disposition routing",
    stops: "Returns pile up uncategorized; refunds delayed; recall scope grows",
    workaround: "Manual triage with paper RMA forms", severity: 3,
    interfaceType: "rest-api", payloadType: "transaction",
    protocol: "Returns workflow API · scanner ingest" },
  { id: 12, l1: "Material", l2: "Reverse / Disposal", l3: "Waste / hazardous",
    examples: "Swarf, slag, contaminated coolant, solvent waste, e-waste",
    source: "Cells, sumps, collection drums", destination: "Treatment, recycling, disposal",
    required: "Chip conveyor, central swarf system, drum-tagging, manifests",
    stops: "Cells back up with swarf; fluid contamination; environmental risk",
    workaround: "Manual emptying with hand carts; paper manifest", severity: 4,
    interfaceType: "material-handling", payloadType: "physical",
    protocol: "Chip conveyor · drum-tagging · e-manifest" },

  // ─── Material · Environmental ──────────────────────────────────────
  { id: 13, l1: "Material", l2: "Environmental Inputs", l3: "Air & climate",
    examples: "HVAC supply, dust/fume extraction, cleanroom air",
    source: "AHU, dust collector, FFU", destination: "Cells, cleanrooms, operator zones",
    required: "Ducting, filters, monitoring",
    stops: "Air quality drops; particulate contamination; operator exposure",
    workaround: "Open windows / fans (often illegal/insufficient)", severity: 4,
    interfaceType: "pneumatic", payloadType: "fluid",
    protocol: "Ducting · HEPA · particle monitor" },
  { id: 14, l1: "Material", l2: "Environmental Inputs", l3: "Water",
    examples: "DI water, process water, potable, cooling water",
    source: "Utility plant, RO system", destination: "Process equipment, chillers",
    required: "Piping, quality monitoring",
    stops: "Cooling fails; rinse processes stop; cleanrooms compromised",
    workaround: "Drums of bottled water - not viable at scale", severity: 4,
    interfaceType: "piping", payloadType: "fluid",
    protocol: "Process piping · conductivity meter" },

  // ─── Energy · Electrical ───────────────────────────────────────────
  { id: 15, l1: "Energy", l2: "Electrical", l3: "AC mains power",
    examples: "3-phase 400/480 V industrial, single-phase 120/230 V",
    source: "MV/LV transformers, MCC", destination: "Drives, motors, heaters, panels",
    required: "AC power bus, switchgear, MCC, distribution boards",
    stops: "Everything stops; cold restart may take hours",
    workaround: "Backup generator (limited capacity, fuel-bound)", severity: 5,
    interfaceType: "electrical-power", payloadType: "energy",
    protocol: "3-phase AC · switchgear · MCC" },
  { id: 16, l1: "Energy", l2: "Electrical", l3: "DC control / servo bus",
    examples: "24 V control, 48 V, 600 V servo DC link",
    source: "PSUs, drive rectifiers", destination: "PLCs, sensors, servo drives",
    required: "DC bus, terminal blocks, common DC bus",
    stops: "Controls dead; servos lose torque; safe stops trigger",
    workaround: "Battery-backed UPS for critical control only", severity: 5,
    interfaceType: "electrical-power", payloadType: "energy",
    protocol: "24 VDC control · servo DC link" },
  { id: 17, l1: "Energy", l2: "Electrical", l3: "Power-over-cable",
    examples: "PoE/PoE+, USB power, sensor loop power",
    source: "PoE switches, PSUs", destination: "IP cameras, edge nodes, transmitters",
    required: "PoE injectors / switches, cable",
    stops: "Edge devices offline; cameras dark; transmitters dead",
    workaround: "Local wall-wart adapters", severity: 3,
    interfaceType: "electrical-power", payloadType: "energy",
    protocol: "PoE/PoE+ (802.3af/at)" },

  // ─── Energy · Mechanical ───────────────────────────────────────────
  { id: 18, l1: "Energy", l2: "Mechanical", l3: "Rotary shaft power",
    examples: "Spindle drive, conveyor drive, pump shaft, fan",
    source: "Motors, gearboxes", destination: "Spindles, conveyors, pumps",
    required: "Couplings, bearings, gearboxes, belts",
    stops: "No rotation; cutting/conveying/pumping all stop",
    workaround: "Manual hand-crank (only for setup, not production)", severity: 5,
    interfaceType: "mechanical", payloadType: "energy",
    protocol: "Coupling · bearing · gearbox" },
  { id: 19, l1: "Energy", l2: "Mechanical", l3: "Linear thrust / motion",
    examples: "Press ram, linear-motor slide, ball-screw axis",
    source: "Linear motors, ball-screws, hydraulics", destination: "Slides, axes, presses",
    required: "Linear guides, ball-screws, profiled rails",
    stops: "Axes don't move; presses don't cycle",
    workaround: "Manual jack / hand-wheel for setup only", severity: 5,
    interfaceType: "mechanical", payloadType: "energy",
    protocol: "Ball-screw · linear guide · profiled rail" },

  // ─── Energy · Thermal ──────────────────────────────────────────────
  { id: 20, l1: "Energy", l2: "Thermal", l3: "Process heating",
    examples: "Furnace, IR, induction, resistance, reflow oven",
    source: "Heating elements, induction coils, IR lamps", destination: "Workpieces, molds, ovens",
    required: "Heating elements, induction coils, ducting",
    stops: "Heat treat aborts; molds cool; cure cycles fail",
    workaround: "Localized electric heat guns (toy-scale)", severity: 4,
    interfaceType: "thermal", payloadType: "energy",
    protocol: "Resistance · induction · IR · convection" },
  { id: 21, l1: "Energy", l2: "Thermal", l3: "Process cooling",
    examples: "Spindle cooling, servo cabinet, mold chiller, cryo",
    source: "Chillers, cold plates", destination: "Cells, drives, molds",
    required: "Cooling channels, jackets, plate HX, TIM",
    stops: "Thermal runaway; tool life drops; molds stick",
    workaround: "Open doors and fans (poor; affects accuracy)", severity: 5,
    interfaceType: "thermal", payloadType: "energy",
    protocol: "Chilled water · cold plate · TIM" },
  { id: 22, l1: "Energy", l2: "Thermal", l3: "Ambient HVAC heat removal",
    examples: "Building HVAC, server-room AC, control-room",
    source: "Plant HVAC", destination: "All occupied / equipment zones",
    required: "Ducting, fans, chilled water",
    stops: "Cabinets overheat; electronics derate; operator stress",
    workaround: "Portable AC units", severity: 3,
    interfaceType: "thermal", payloadType: "energy",
    protocol: "Building HVAC · chilled water loop" },

  // ─── Energy · EM/Optical ───────────────────────────────────────────
  { id: 23, l1: "Energy", l2: "EM / Optical", l3: "Coherent light",
    examples: "Fiber laser beam for cutting / welding / marking",
    source: "Fiber laser source", destination: "Optics, scanner heads, workpiece",
    required: "Single-mode fiber, scanners, focus heads",
    stops: "Laser processing stops",
    workaround: "Alternate process (plasma, mechanical) if available", severity: 5,
    interfaceType: "optical", payloadType: "energy",
    protocol: "Single-mode fiber · galvo scanner" },
  { id: 24, l1: "Energy", l2: "EM", l3: "Radio-frequency",
    examples: "Induction hardening, RF plasma, RF heating",
    source: "RF generator", destination: "Coils, plasma chambers",
    required: "Coax, waveguides, matching networks",
    stops: "Hardening, deposition, plasma processes stop",
    workaround: "No equivalent at scale", severity: 5,
    interfaceType: "optical", payloadType: "energy",
    protocol: "Coax · waveguide · matching network" },

  // ─── Energy · Acoustic ─────────────────────────────────────────────
  { id: 25, l1: "Energy", l2: "Acoustic", l3: "Ultrasonic",
    examples: "Ultrasonic welding, cleaning, NDT",
    source: "US generator", destination: "Sonotrode, tanks",
    required: "Horn coupling, sonotrode mount",
    stops: "Plastic welds fail; parts not cleaned; NDT stops",
    workaround: "Solvent cleaning, mechanical fasteners", severity: 3,
    interfaceType: "mechanical", payloadType: "energy",
    protocol: "Sonotrode horn coupling" },

  // ─── Energy · Stored ───────────────────────────────────────────────
  { id: 26, l1: "Energy", l2: "Stored", l3: "Spring / accumulator / battery",
    examples: "Hydraulic accumulators, pneumatic reservoirs, UPS",
    source: "Charging system", destination: "Critical actuators, controls",
    required: "Charge / discharge interfaces",
    stops: "Safe-stop fails; ride-through gone; emergency functions degraded",
    workaround: "Manual safety actuation", severity: 4,
    interfaceType: "electrical-power", payloadType: "energy",
    protocol: "Accumulator · UPS · battery bank" },

  // ─── Signal · Analog ───────────────────────────────────────────────
  { id: 27, l1: "Signal", l2: "Analog", l3: "DC voltage",
    examples: "0-10 V, +/-10 V sensor outputs and setpoints",
    source: "Transmitters, potentiometers", destination: "PLC/DAQ AI cards",
    required: "Twisted pair + AI module",
    stops: "Pressure/level/flow/setpoints invisible; can't close loops",
    workaround: "Local gauge reading; manual adjust", severity: 5,
    interfaceType: "analog-io", payloadType: "electrical",
    protocol: "0-10 V · +/-10 V · twisted pair" },
  { id: 28, l1: "Signal", l2: "Analog", l3: "Current loop",
    examples: "4-20 mA, 4-20 mA HART",
    source: "Process transmitters", destination: "PLC AI cards",
    required: "Twisted pair (loop-powered)",
    stops: "Same as above + HART config / diagnostics also lost",
    workaround: "Local indicator on transmitter", severity: 5,
    interfaceType: "analog-io", payloadType: "electrical",
    protocol: "4-20 mA · HART superimposed" },
  { id: 29, l1: "Signal", l2: "Analog", l3: "Resistance / EMF",
    examples: "RTD (Pt100), thermocouple (K/J/T/N)",
    source: "RTD elements, TC junctions", destination: "TC/RTD input modules",
    required: "Direct twisted pair, compensating cable",
    stops: "No temperature reading; thermal runaway risk",
    workaround: "Hand-held pyrometer spot checks", severity: 5,
    interfaceType: "analog-io", payloadType: "electrical",
    protocol: "Pt100 RTD · TC K/J/T/N" },
  { id: 30, l1: "Signal", l2: "Analog", l3: "Frequency / pulse",
    examples: "Tachometer, flow pulses, magnetic pickup",
    source: "Pulse generators, turbines", destination: "Counter modules",
    required: "Pulse counter input",
    stops: "No flow / speed feedback; throughput calc fails",
    workaround: "Stopwatch + manual count (not feasible at speed)", severity: 4,
    interfaceType: "analog-io", payloadType: "electrical",
    protocol: "Pulse counter input" },

  // ─── Signal · Digital ──────────────────────────────────────────────
  { id: 31, l1: "Signal", l2: "Digital", l3: "Discrete I/O",
    examples: "Limit switch, proximity, photoelectric, relay output",
    source: "Field sensors", destination: "DI cards, machine logic",
    required: "24 VDC discrete wiring",
    stops: "Cycle won't advance; cell loses sequence awareness",
    workaround: "Operator triggers cycle manually", severity: 5,
    interfaceType: "discrete-io", payloadType: "boolean",
    protocol: "24 VDC sourcing/sinking" },
  { id: 32, l1: "Signal", l2: "Digital", l3: "Modulated digital",
    examples: "PWM, PFM outputs to drives / heaters / valves",
    source: "Microcontrollers, drives", destination: "Motors, heaters, valves",
    required: "Direct digital output, drive interface",
    stops: "No proportional control; on/off only",
    workaround: "Manual valve position", severity: 4,
    interfaceType: "discrete-io", payloadType: "electrical",
    protocol: "PWM · PFM" },
  { id: 33, l1: "Signal", l2: "Digital", l3: "Encoded position",
    examples: "Quadrature (A/B/Z), SSI, BiSS-C, EnDat 2.2",
    source: "Encoders", destination: "Drive feedback ports, motion ctrl",
    required: "Differential serial / pair (RS-422)",
    stops: "Servo runs open-loop; runaway risk; safe stop triggers",
    workaround: "No safe alternative for closed-loop motion", severity: 5,
    interfaceType: "discrete-io", payloadType: "scalar",
    protocol: "BiSS-C · EnDat 2.2 · SSI · A/B/Z" },
  { id: 34, l1: "Signal", l2: "Digital", l3: "Safety-rated signals",
    examples: "Dual-channel e-stop, guard switch, light curtain output",
    source: "Safety devices", destination: "Safety relays / F-PLC",
    required: "Hardwired dual-channel safety circuit",
    stops: "No automatic safe-state; injury risk rises sharply",
    workaround: "Lock-out / physical fencing", severity: 5,
    interfaceType: "safety-circuit", payloadType: "boolean",
    protocol: "Dual-channel hardwired (Cat 3/4 PLd/PLe)" },

  // ─── Data · Telemetry ──────────────────────────────────────────────
  { id: 35, l1: "Data", l2: "Telemetry", l3: "Time-series single-point",
    examples: "Tank level, pressure, flow, temperature scans",
    source: "PLCs, drives, smart sensors", destination: "SCADA, historian, edge gateway",
    required: "Industrial Ethernet, OPC UA, Modbus TCP",
    stops: "No process history; trending / PdM / SPC all blind",
    workaround: "Periodic manual log sheets", severity: 4,
    interfaceType: "industrial-ethernet", payloadType: "scalar",
    protocol: "OPC UA · Modbus TCP" },
  { id: 36, l1: "Data", l2: "Telemetry", l3: "Time-series multi-tag",
    examples: "Cell KPIs streamed via OPC UA subscription",
    source: "OPC UA servers (PLC/cell)", destination: "Historian, MES, twin",
    required: "OPC UA PubSub / DA",
    stops: "OEE/yield/availability invisible; no analytics input",
    workaround: "Spreadsheets compiled weekly", severity: 3,
    interfaceType: "industrial-ethernet", payloadType: "structured",
    protocol: "OPC UA PubSub · DA subscription" },
  { id: 37, l1: "Data", l2: "Telemetry", l3: "Waveform / high-frequency",
    examples: "Vibration spectra, motor current signature, AE",
    source: "Accelerometers, current sensors", destination: "Edge analytics, PdM platform",
    required: "Edge gateway, IIoT analytics box",
    stops: "No predictive maintenance; bearing failures surprise",
    workaround: "Route-based handheld vibration meter", severity: 4,
    interfaceType: "industrial-ethernet", payloadType: "vector",
    protocol: "MQTT · edge analytics binary" },
  { id: 38, l1: "Data", l2: "Telemetry", l3: "Aggregated KPIs",
    examples: "OEE, yield, RTY, FPY rollups",
    source: "MES, historian", destination: "Dashboards, ERP, cloud",
    required: "REST/GraphQL, OPC UA",
    stops: "No KPIs visible; management flies blind",
    workaround: "Manual spreadsheet rollup", severity: 3,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "REST/JSON · GraphQL · OPC UA" },

  // ─── Data · Event ──────────────────────────────────────────────────
  { id: 39, l1: "Data", l2: "Event", l3: "State change / discrete event",
    examples: "Start, stop, mode change, recipe load",
    source: "PLC, machine controller", destination: "SCADA, MES, historian",
    required: "OPC UA A&C, MQTT",
    stops: "No cycle counting; no event-driven workflow",
    workaround: "Operator paper logs", severity: 4,
    interfaceType: "messaging", payloadType: "event",
    protocol: "OPC UA A&C · MQTT" },
  { id: 40, l1: "Data", l2: "Event", l3: "Process alarm",
    examples: "ISA-18.2 alarm records, severity-classed events",
    source: "PLC, SCADA", destination: "HMI, alarm historian",
    required: "OPC UA A&C, alarm bus",
    stops: "Alarm flood reduces to klaxon-only; root cause invisible",
    workaround: "Operator listens for horns", severity: 5,
    interfaceType: "messaging", payloadType: "event",
    protocol: "OPC UA A&C · ISA-18.2 envelope" },
  { id: 41, l1: "Data", l2: "Event", l3: "Audit / compliance event",
    examples: "21 CFR Part 11 e-signature, change records",
    source: "MES, QMS, LIMS", destination: "Audit DB, regulators",
    required: "Database / REST, append-only ledger",
    stops: "No audit trail; GxP / regulatory non-compliance",
    workaround: "Paper logbooks with wet signatures", severity: 5,
    interfaceType: "database", payloadType: "event",
    protocol: "Append-only ledger · REST · DB" },

  // ─── Data · Configuration ──────────────────────────────────────────
  { id: 42, l1: "Data", l2: "Configuration", l3: "Device parameters",
    examples: "Servo gains, drive params, sensor config, vision matrices",
    source: "Engineering tools, MES", destination: "Drives, controllers, sensors",
    required: "Engineering port, OPC UA, file transfer",
    stops: "Cannot tune, configure, or swap devices",
    workaround: "Manual programming-pendant config", severity: 4,
    interfaceType: "file-exchange", payloadType: "structured",
    protocol: "OPC UA · vendor engineering port" },
  { id: 43, l1: "Data", l2: "Configuration", l3: "Firmware / binary",
    examples: "PLC / drive / edge firmware images",
    source: "Vendor portal, IT", destination: "Devices",
    required: "Engineering port, OTA, signed bundle",
    stops: "Cannot patch; security and feature debt grows",
    workaround: "USB-stick local update", severity: 3,
    interfaceType: "file-exchange", payloadType: "document",
    protocol: "OTA · signed firmware bundle" },
  { id: 44, l1: "Data", l2: "Configuration", l3: "Calibration data",
    examples: "Encoder zero, robot tool frames, vision homography",
    source: "Cal tool, metrology lab", destination: "Sensors, controllers",
    required: "Engineering port, file",
    stops: "Drift uncorrected; accuracy degrades over time",
    workaround: "Sneakernet of cal certificates", severity: 4,
    interfaceType: "file-exchange", payloadType: "structured",
    protocol: "Cal certificate file · engineering port" },

  // ─── Data · Master ─────────────────────────────────────────────────
  { id: 45, l1: "Data", l2: "Master", l3: "Material master",
    examples: "SKU, item record, BOM, packaging",
    source: "ERP", destination: "MES, PLM, WMS",
    required: "REST, IDoc, MDM message bus",
    stops: "Wrong materials picked; BOM mismatches",
    workaround: "Manually maintained item list per system", severity: 4,
    interfaceType: "messaging", payloadType: "structured",
    protocol: "IDoc · REST · MDM message bus" },
  { id: 46, l1: "Data", l2: "Master", l3: "Engineering BOM <-> mfg BOM",
    examples: "eBOM, mBOM, ECO, effectivity",
    source: "PLM", destination: "ERP, MES",
    required: "REST, OSLC, file exchange",
    stops: "Build to wrong revision; ECO chaos",
    workaround: "Paper-controlled drawings package", severity: 4,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "REST · OSLC · file exchange" },
  { id: 47, l1: "Data", l2: "Master", l3: "Routing / process plan",
    examples: "Operations sequence, work centres, cycle times",
    source: "MES, ERP", destination: "Cell controllers, MES dispatch",
    required: "REST, B2MML",
    stops: "Dispatch becomes manual; sequence errors",
    workaround: "Paper traveler with stamps", severity: 4,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "REST · B2MML" },
  { id: 48, l1: "Data", l2: "Master", l3: "Resource / asset master",
    examples: "Machine, cell, tool, fixture definitions",
    source: "ERP, EAM", destination: "MES, OEE, CMMS",
    required: "REST, IDoc",
    stops: "Asset registry stale; maintenance schedules drift",
    workaround: "Spreadsheet per plant", severity: 3,
    interfaceType: "messaging", payloadType: "structured",
    protocol: "REST · IDoc" },

  // ─── Data · Command ────────────────────────────────────────────────
  { id: 49, l1: "Data", l2: "Command", l3: "Setpoint",
    examples: "Process variable target (temp, pressure, flow, speed)",
    source: "SCADA, MES recipe", destination: "PLCs, controllers, drives",
    required: "OPC UA, fieldbus",
    stops: "Process won't track recipe; off-spec output",
    workaround: "Manual local-panel setpoint entry", severity: 5,
    interfaceType: "industrial-ethernet", payloadType: "command",
    protocol: "OPC UA write · fieldbus PDO" },
  { id: 50, l1: "Data", l2: "Command", l3: "Motion command",
    examples: "Joint / Cartesian / tool-frame target paths",
    source: "Motion controller, robot controller", destination: "Servo drives",
    required: "EtherCAT, PROFINET IRT, Sercos",
    stops: "No coordinated motion; robots/CNC stop",
    workaround: "Jog mode with pendant (setup only)", severity: 5,
    interfaceType: "fieldbus", payloadType: "command",
    protocol: "EtherCAT · PROFINET IRT · Sercos III" },
  { id: 51, l1: "Data", l2: "Command", l3: "Recipe / parameter set",
    examples: "ISA-88 batch recipe, IMM cycle parameters",
    source: "MES, recipe manager", destination: "Cell controller, PLC",
    required: "OPC UA, REST + file",
    stops: "Wrong recipe runs; lot mix-ups; quality fail",
    workaround: "Operator enters recipe by hand from printout", severity: 5,
    interfaceType: "industrial-ethernet", payloadType: "command",
    protocol: "OPC UA · REST · ISA-88 recipe" },
  { id: 52, l1: "Data", l2: "Command", l3: "Mode / state command",
    examples: "PackML state (start, stop, clear, abort), production mode",
    source: "SCADA, MES", destination: "Machine controllers",
    required: "OPC UA PackML companion",
    stops: "Line cannot be coordinated; manual mode only",
    workaround: "Walk to each HMI and press buttons", severity: 4,
    interfaceType: "industrial-ethernet", payloadType: "command",
    protocol: "OPC UA PackML companion spec" },

  // ─── Data · Transactional ──────────────────────────────────────────
  { id: 53, l1: "Data", l2: "Transactional", l3: "Production order",
    examples: "Discrete / process order release",
    source: "ERP", destination: "MES",
    required: "B2MML over MQ/REST",
    stops: "Work orders don't reach the line; production stalls",
    workaround: "Paper WO printed from ERP", severity: 4,
    interfaceType: "messaging", payloadType: "transaction",
    protocol: "B2MML over MQ/REST · IDoc" },
  { id: 54, l1: "Data", l2: "Transactional", l3: "Inventory movement",
    examples: "Goods movement (issues, receipts, transfers, backflush)",
    source: "MES, WMS, scanners", destination: "ERP",
    required: "REST, IDoc, scanner",
    stops: "Inventory ledger drifts; finance wrong",
    workaround: "Periodic physical inventory, paper movements", severity: 4,
    interfaceType: "messaging", payloadType: "transaction",
    protocol: "IDoc · REST · scanner ingest" },
  { id: 55, l1: "Data", l2: "Transactional", l3: "Operation confirmation",
    examples: "Op complete, yield, scrap, runtime",
    source: "MES, machine", destination: "ERP, finance",
    required: "B2MML, REST",
    stops: "No confirmation -> WIP and COGS go wrong",
    workaround: "Daily manual confirmation entry", severity: 4,
    interfaceType: "messaging", payloadType: "transaction",
    protocol: "B2MML · REST · BAPI" },
  { id: 56, l1: "Data", l2: "Transactional", l3: "B2B documents",
    examples: "EDI X12 856 ASN, 850 PO, 810 invoice",
    source: "Suppliers, customers", destination: "ERP",
    required: "EDI VAN, AS2, AS4",
    stops: "Inbound / outbound transactions lost; rec & invoice errors",
    workaround: "Phone, email, fax of paper documents", severity: 4,
    interfaceType: "b2b-gateway", payloadType: "transaction",
    protocol: "EDI X12 · EDIFACT · AS2/AS4" },

  // ─── Data · Engineering ────────────────────────────────────────────
  { id: 57, l1: "Data", l2: "Engineering", l3: "3D CAD geometry",
    examples: "STEP AP242, JT, Parasolid",
    source: "CAD (NX, CATIA, Creo)", destination: "CAM, CMM, MES, twin",
    required: "File exchange, PLM API",
    stops: "CAM / inspection can't start; programs out of date",
    workaround: "Sneakernet via USB; paper drawings", severity: 4,
    interfaceType: "file-exchange", payloadType: "geometry-3d",
    protocol: "STEP AP242 · JT · Parasolid" },
  { id: 58, l1: "Data", l2: "Engineering", l3: "2D drawing",
    examples: "DXF / DWG",
    source: "CAD", destination: "Laser / waterjet / plasma cutters",
    required: "File exchange",
    stops: "Cutting machines idle or run wrong file",
    workaround: "Print and re-key DXF by hand (impractical)", severity: 4,
    interfaceType: "file-exchange", payloadType: "geometry-2d",
    protocol: "DXF · DWG" },
  { id: 59, l1: "Data", l2: "Engineering", l3: "GD&T / PMI",
    examples: "PMI in STEP AP242, QIF",
    source: "CAD", destination: "CMM, CAM, inspection planner",
    required: "Neutral file + QIF",
    stops: "Inspection planned wrong; tolerances missed",
    workaround: "Paper drawing notes, manual programming", severity: 4,
    interfaceType: "file-exchange", payloadType: "geometry-3d",
    protocol: "STEP AP242 PMI · QIF" },
  { id: 60, l1: "Data", l2: "Engineering", l3: "Toolpath / NC program",
    examples: "G-code (ISO 6983), STEP-NC, APT",
    source: "CAM", destination: "CNC controllers",
    required: "File transfer, DNC, network share",
    stops: "Machines can't run posted programs",
    workaround: "Hand-key small programs at HMI", severity: 5,
    interfaceType: "file-exchange", payloadType: "document",
    protocol: "ISO 6983 G-code · STEP-NC · DNC" },
  { id: 61, l1: "Data", l2: "Engineering", l3: "Simulation / FMU",
    examples: "FEA, CFD, process sim, FMI/FMU result decks",
    source: "CAE tools", destination: "Twin, design review",
    required: "File / PLM, twin platform",
    stops: "Twin diverges from reality; design verification slows",
    workaround: "Standalone CAE workstations", severity: 2,
    interfaceType: "file-exchange", payloadType: "model",
    protocol: "FMI/FMU · vendor decks" },

  // ─── Data · Quality ────────────────────────────────────────────────
  { id: 62, l1: "Data", l2: "Quality", l3: "Inspection results",
    examples: "QIF results, DMIS programs and results",
    source: "CMM, laser scanner, in-line gauge", destination: "SPC, MES, twin, CAM",
    required: "QIF over file / OPC UA",
    stops: "Closed-loop CMM->CAM breaks; no auto comp",
    workaround: "Paper inspection report; manual re-offset", severity: 5,
    interfaceType: "file-exchange", payloadType: "structured",
    protocol: "QIF · DMIS · OPC UA companion" },
  { id: 63, l1: "Data", l2: "Quality", l3: "SPC subgroups",
    examples: "X-bar, R, Cp, Cpk, Western Electric rules",
    source: "MES, SPC system", destination: "Process control logic, dashboards",
    required: "REST, OPC UA",
    stops: "No statistical control; drift undetected",
    workaround: "Hand-calculated paper charts", severity: 4,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "REST · OPC UA" },
  { id: 64, l1: "Data", l2: "Quality", l3: "Genealogy / traceability",
    examples: "Lot / serial parent-child chain, as-built",
    source: "MES, scanners, vision", destination: "Quality, recall, ERP",
    required: "MES DB, REST",
    stops: "Cannot bound recalls; IATF/ISO 22005 broken",
    workaround: "Paper batch records (slow, error-prone)", severity: 5,
    interfaceType: "database", payloadType: "structured",
    protocol: "Graph DB · REST · MES API" },
  { id: 65, l1: "Data", l2: "Quality", l3: "Defect / CAPA",
    examples: "NCR, 8D, CAPA records",
    source: "Quality, operators", destination: "QMS",
    required: "QMS workflow",
    stops: "Defects don't drive learning; recurring escapes",
    workaround: "Email + spreadsheet CAPA tracker", severity: 3,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "QMS workflow · REST" },

  // ─── Data · Identification ─────────────────────────────────────────
  { id: 66, l1: "Data", l2: "Identification", l3: "Optical codes",
    examples: "GS1-128 barcode, Data Matrix, QR, UDI",
    source: "Print stations, supplier labels", destination: "Scanners, vision",
    required: "Scanner (HID/serial/Ethernet)",
    stops: "Parts not identified; everything is anonymous",
    workaround: "Hand-write IDs and key them in", severity: 5,
    interfaceType: "scanner-hid", payloadType: "structured",
    protocol: "GS1-128 · Data Matrix · QR · UDI" },
  { id: 67, l1: "Data", l2: "Identification", l3: "RFID",
    examples: "EPC Gen2 UHF, ISO 14443 HF tags",
    source: "Tagged assets, totes, tools", destination: "Readers, gates",
    required: "Reader (LLRP, vendor)",
    stops: "Unattended tracking impossible; tool ID lost",
    workaround: "Manual log of asset movements", severity: 3,
    interfaceType: "wireless", payloadType: "structured",
    protocol: "EPC Gen2 (LLRP) · ISO 14443" },
  { id: 68, l1: "Data", l2: "Identification", l3: "Unique IDs",
    examples: "Serial, UDI, DUNS, VIN",
    source: "Marking stations", destination: "All downstream",
    required: "Marking + read-back chain",
    stops: "Parts shipped without IDs; recall impossible",
    workaround: "Hand-engrave / hand-write IDs", severity: 5,
    interfaceType: "scanner-hid", payloadType: "structured",
    protocol: "Marker + verification scanner" },

  // ─── Data · Media-Image ────────────────────────────────────────────
  { id: 69, l1: "Data", l2: "Media - Image", l3: "Still vision frame",
    examples: "Bayer / mono frames (TIFF, PNG, RAW)",
    source: "Industrial cameras", destination: "Vision systems, edge AI",
    required: "GigE Vision, USB3 Vision, CXP",
    stops: "Vision inspection stops; defects escape",
    workaround: "Visual operator inspection", severity: 4,
    interfaceType: "vision", payloadType: "media-image",
    protocol: "GigE Vision · USB3 Vision · CXP" },
  { id: 70, l1: "Data", l2: "Media - Image", l3: "3D point cloud",
    examples: "PLY, E57, LAS scan data",
    source: "3D scanners", destination: "Metrology SW, twin",
    required: "File transfer, GigE",
    stops: "No 3D metrology; weld bead inspection blind",
    workaround: "Manual gauging", severity: 4,
    interfaceType: "vision", payloadType: "media-image",
    protocol: "PLY · E57 · LAS" },
  { id: 71, l1: "Data", l2: "Media - Image", l3: "NDT image (X-ray/CT)",
    examples: "DICONDE, DICOM-like images",
    source: "X-ray / CT systems", destination: "NDT review SW",
    required: "DICOM/DICONDE network",
    stops: "No NDT review; porosity / cracks escape",
    workaround: "Film-based NDT (slow, hazardous)", severity: 4,
    interfaceType: "vision", payloadType: "media-image",
    protocol: "DICONDE · DICOM" },

  // ─── Data · Media-Video ────────────────────────────────────────────
  { id: 72, l1: "Data", l2: "Media - Video", l3: "Live stream",
    examples: "RTSP H.264/H.265 from IP cameras",
    source: "IP cameras", destination: "NVR, vision, operators",
    required: "IP/RTSP",
    stops: "No remote eyes; safety / security monitoring gone",
    workaround: "Walk-the-floor by supervisors", severity: 3,
    interfaceType: "vision", payloadType: "media-video",
    protocol: "RTSP · H.264/H.265" },

  // ─── Data · Media-Audio ────────────────────────────────────────────
  { id: 73, l1: "Data", l2: "Media - Audio", l3: "Voice / ASR",
    examples: "Operator voice commands, headset audio",
    source: "Headsets, mics", destination: "Voice control, ASR",
    required: "Audio I/F + NLU",
    stops: "Pick-by-voice fails; hands-free workflows stop",
    workaround: "Paper picklists, radios", severity: 2,
    interfaceType: "audio", payloadType: "media-audio",
    protocol: "Audio I/F · ASR · NLU API" },
  { id: 74, l1: "Data", l2: "Media - Audio", l3: "Acoustic emission",
    examples: "Machine acoustic signature, AE",
    source: "AE sensors", destination: "PdM analytics",
    required: "DAQ, analytics chain",
    stops: "No acoustic PdM; gearbox failures surprise",
    workaround: "Operator listens for unusual sounds", severity: 3,
    interfaceType: "audio", payloadType: "vector",
    protocol: "DAQ · acoustic spectrum" },

  // ─── Data · Media-Document ─────────────────────────────────────────
  { id: 75, l1: "Data", l2: "Media - Document", l3: "Procedures & certificates",
    examples: "SOPs, MTR, CoC, MSDS, work instructions",
    source: "QMS, vendors", destination: "Operators, auditors",
    required: "DMS, file share, REST",
    stops: "Operators work to outdated SOPs; audit fail",
    workaround: "Binder of paper SOPs at station", severity: 3,
    interfaceType: "rest-api", payloadType: "document",
    protocol: "DMS · file share · REST" },

  // ─── Data · Semantic ───────────────────────────────────────────────
  { id: 76, l1: "Data", l2: "Semantic", l3: "Information model",
    examples: "OPC UA address space, AAS submodels",
    source: "Vendor servers, OEMs", destination: "SCADA, MES, twin",
    required: "OPC UA browse + read",
    stops: "No semantic context; data without meaning",
    workaround: "Hand-maintained tag dictionary", severity: 3,
    interfaceType: "industrial-ethernet", payloadType: "structured",
    protocol: "OPC UA address space · AAS submodel" },
  { id: 77, l1: "Data", l2: "Semantic", l3: "Schema / contract",
    examples: "Avro, Protobuf, JSON Schema, XSD",
    source: "API designers", destination: "Producers + consumers",
    required: "Schema registry",
    stops: "Producers and consumers drift; data breaks silently",
    workaround: "Documented contracts in wikis", severity: 3,
    interfaceType: "rest-api", payloadType: "document",
    protocol: "Schema registry · Avro · Protobuf · JSON Schema" },
  { id: 78, l1: "Data", l2: "Semantic", l3: "ML model artifact",
    examples: "ONNX, PMML, TensorFlow SavedModel",
    source: "Data-science platforms", destination: "Edge inference, cloud",
    required: "Model registry, OTA",
    stops: "No model updates; predictions decay",
    workaround: "Manual model file copy", severity: 2,
    interfaceType: "file-exchange", payloadType: "model",
    protocol: "Model registry · ONNX · PMML · SavedModel" },
  { id: 79, l1: "Data", l2: "Semantic", l3: "Inference output",
    examples: "Class label, probability, anomaly score",
    source: "Edge ML, cloud ML", destination: "MES, alarms, twin",
    required: "MQTT, OPC UA, REST",
    stops: "No ML feedback into operations",
    workaround: "Rules-only fallback", severity: 2,
    interfaceType: "messaging", payloadType: "structured",
    protocol: "MQTT · OPC UA · REST" },

  // ─── Control ───────────────────────────────────────────────────────
  { id: 80, l1: "Control", l2: "Inner Loop", l3: "PID at drive level",
    examples: "Position, velocity, torque control loops",
    source: "Setpoint source", destination: "Drive PID block",
    required: "Drive command interface, encoder feedback",
    stops: "Servo runs open-loop or coasts; no precision",
    workaround: "Mechanical detents only (no closed loop)", severity: 5,
    interfaceType: "fieldbus", payloadType: "command",
    protocol: "Drive command · encoder feedback (closed loop)" },
  { id: 81, l1: "Control", l2: "Mid Loop", l3: "Process control loop",
    examples: "Temperature, pressure, level, flow PID at PLC",
    source: "PLC PID block", destination: "Final control element",
    required: "Sensor->PLC->actuator chain (OPC UA, fieldbus)",
    stops: "Process drifts off setpoint; manual chase",
    workaround: "Operator chases process by hand", severity: 5,
    interfaceType: "industrial-ethernet", payloadType: "command",
    protocol: "OPC UA · fieldbus · PID block" },
  { id: 82, l1: "Control", l2: "Outer Loop", l3: "SPC / quality feedback",
    examples: "Auto-hold on Western Electric rules, auto compensation",
    source: "SPC engine", destination: "Process control, dispatcher",
    required: "REST, OPC UA",
    stops: "Drift to OOS; defects ship",
    workaround: "Operator-triggered manual review", severity: 5,
    interfaceType: "rest-api", payloadType: "command",
    protocol: "REST · OPC UA write-back" },
  { id: 83, l1: "Control", l2: "Outermost Loop", l3: "Quality->planning",
    examples: "Yield-driven scheduling, recipe re-issue, route changes",
    source: "Quality / MES", destination: "ERP scheduler",
    required: "MES<->ERP integration",
    stops: "Repeated escapes; no learning",
    workaround: "Quarterly review meetings", severity: 3,
    interfaceType: "messaging", payloadType: "command",
    protocol: "MES <-> ERP integration · REST" },
  { id: 84, l1: "Control", l2: "Adaptive", l3: "Model-in-the-loop",
    examples: "MPC, RL, ML-based adaptive control",
    source: "Model registry, edge inference", destination: "Drives, controllers",
    required: "OPC UA write-back, MQTT, REST",
    stops: "No adaptive control; static recipes only",
    workaround: "Engineering manual re-tuning", severity: 3,
    interfaceType: "industrial-ethernet", payloadType: "command",
    protocol: "OPC UA write · MQTT · REST" },
  { id: 85, l1: "Control", l2: "Closed-loop CAD<->CMM", l3: "Geometry correction",
    examples: "Auto-compensate toolpath from metrology deviation",
    source: "CMM / scanner", destination: "CAM, CNC",
    required: "QIF + CAM API",
    stops: "Geometric drift uncorrected; scrap until manual fix",
    workaround: "Engineer reviews CMM and edits CAM", severity: 4,
    interfaceType: "rest-api", payloadType: "command",
    protocol: "QIF · CAM API" },

  // ─── Safety ────────────────────────────────────────────────────────
  { id: 86, l1: "Safety", l2: "Functional", l3: "Hardwired safety chain",
    examples: "E-stop, gate switch, light-curtain, mat switch",
    source: "Field safety devices", destination: "Safety relay, F-PLC",
    required: "Hardwired dual-channel safety circuit",
    stops: "No automatic safe-state; manual reaction only",
    workaround: "Lock-out / tag-out, physical fencing", severity: 5,
    interfaceType: "safety-circuit", payloadType: "boolean",
    protocol: "Dual-channel hardwired (PLd/PLe)" },
  { id: 87, l1: "Safety", l2: "Functional", l3: "Safety bus telegrams",
    examples: "PROFIsafe, CIP Safety, FSoE F-frames",
    source: "F-PLC", destination: "Safe I/O, safe drives",
    required: "Safety-bus black channel over standard cable",
    stops: "Loss of distributed safety; cells must be hardwired",
    workaround: "Hardwire each safety function locally", severity: 5,
    interfaceType: "safety-bus", payloadType: "command",
    protocol: "PROFIsafe · CIP Safety · FSoE" },
  { id: 88, l1: "Safety", l2: "Functional", l3: "Drive safety state",
    examples: "STO, SS1, SS2, SLS",
    source: "Safety controller", destination: "Safe drives",
    required: "Safety bus / hardwired",
    stops: "Drives can't enter safe-stop autonomously",
    workaround: "Power-disconnect contactor (cruder, slower)", severity: 5,
    interfaceType: "safety-bus", payloadType: "command",
    protocol: "PROFIsafe/CIP Safety · safety bus · hardwired" },

  // ─── Human ─────────────────────────────────────────────────────────
  { id: 89, l1: "Human", l2: "Input", l3: "Tactile - HMI",
    examples: "Touchscreen events, push-buttons, switches",
    source: "Touchscreen, buttons", destination: "HMI app, PLC",
    required: "HID, hardwired discrete",
    stops: "Operator can't drive the machine; reverts to mechanical",
    workaround: "Mechanical hand-wheels, paper checklists", severity: 4,
    interfaceType: "scanner-hid", payloadType: "event",
    protocol: "HID · discrete I/O" },
  { id: 90, l1: "Human", l2: "Input", l3: "E-stop / safety",
    examples: "Hardwired discrete safety event",
    source: "Buttons, e-stops", destination: "Safety / control PLC",
    required: "Hardwired safety circuit",
    stops: "No emergency stop; safety incident risk",
    workaround: "Master disconnect at panel", severity: 5,
    interfaceType: "safety-circuit", payloadType: "boolean",
    protocol: "Dual-channel hardwired e-stop" },
  { id: 91, l1: "Human", l2: "Input", l3: "Scanning",
    examples: "Barcode, QR, RFID scanner triggers",
    source: "Handheld / fixed scanners", destination: "MES, HMI",
    required: "Scanner I/F (HID/serial/Ethernet)",
    stops: "No traceability scan-in; manual entry errors",
    workaround: "Hand-key IDs", severity: 4,
    interfaceType: "scanner-hid", payloadType: "structured",
    protocol: "HID · serial · TCP/IP scanner" },
  { id: 92, l1: "Human", l2: "Input", l3: "Voice",
    examples: "Voice command, headset audio",
    source: "Headset / mic", destination: "Voice service, MES",
    required: "Voice gateway, NLU",
    stops: "Hands-free workflows die",
    workaround: "Two-hand paper-driven workflow", severity: 2,
    interfaceType: "audio", payloadType: "media-audio",
    protocol: "Voice gateway · NLU API" },
  { id: 93, l1: "Human", l2: "Output", l3: "Visual",
    examples: "HMI screen, dashboards, AR overlays",
    source: "HMI runtime", destination: "Operator eyes",
    required: "HMI panel, web HMI, AR headset",
    stops: "Operator blind to state; works on memory",
    workaround: "Paper kanban, whiteboards", severity: 4,
    interfaceType: "scanner-hid", payloadType: "structured",
    protocol: "HMI panel · web HMI · AR overlay" },
  { id: 94, l1: "Human", l2: "Output", l3: "Audible",
    examples: "Alarm tones, voice annunciators, stack-light buzzers",
    source: "Alarm system", destination: "Operator ears",
    required: "Hardwired / OPC UA -> IO",
    stops: "Critical alarms missed",
    workaround: "Shouted alarms by supervisor", severity: 5,
    interfaceType: "discrete-io", payloadType: "boolean",
    protocol: "Hardwired stack light · OPC UA -> annunciator" },
  { id: 95, l1: "Human", l2: "Output", l3: "Haptic",
    examples: "Wearable haptics, cobot guidance vibration",
    source: "Cobot / wearable", destination: "Operator body",
    required: "BLE, vendor I/F",
    stops: "Cobot teach-in is harder; subtle alerts missed",
    workaround: "Verbal coaching", severity: 2,
    interfaceType: "wireless", payloadType: "event",
    protocol: "BLE · vendor wearable API" },
  { id: 96, l1: "Human", l2: "Identity", l3: "Operator badge / biometric",
    examples: "RFID badge tap, fingerprint, face login",
    source: "Reader, biometric", destination: "IAM, MES",
    required: "Reader I/F, IAM API",
    stops: "No personalized recipes; no accountability",
    workaround: "Username/password manual login", severity: 3,
    interfaceType: "iam", payloadType: "credential",
    protocol: "RFID reader · biometric · IAM API" },

  // ─── Security ──────────────────────────────────────────────────────
  { id: 97, l1: "Security", l2: "Credential", l3: "Bearer token",
    examples: "OAuth 2.0 / JWT for API auth",
    source: "IdP (Okta, Entra)", destination: "APIs, services",
    required: "HTTPS / OIDC",
    stops: "API auth broken; either insecure or offline",
    workaround: "Long-lived static API keys (insecure)", severity: 4,
    interfaceType: "iam", payloadType: "credential",
    protocol: "OAuth 2.0 · OIDC · JWT" },
  { id: 98, l1: "Security", l2: "Credential", l3: "Device certificate",
    examples: "X.509 device / app certs",
    source: "PKI / CA", destination: "OPC UA servers, devices",
    required: "PKI provisioning workflow",
    stops: "Mutual TLS fails; OPC UA can't establish secure channel",
    workaround: "Disable TLS (illegal under IEC 62443)", severity: 5,
    interfaceType: "pki", payloadType: "credential",
    protocol: "X.509 · PKI provisioning · mTLS" },
  { id: 99, l1: "Security", l2: "Crypto", l3: "Key material",
    examples: "AES, RSA, ECC keys",
    source: "HSM, KMS", destination: "Devices, services",
    required: "KMS / HSM API",
    stops: "No envelope encryption, no signing, no auth",
    workaround: "Plaintext storage (not viable)", severity: 5,
    interfaceType: "pki", payloadType: "key-material",
    protocol: "HSM · KMS API · AES/RSA/ECC" },
  { id: 100, l1: "Security", l2: "Crypto", l3: "Digital signature",
    examples: "Code-sign, document sign",
    source: "Signing service", destination: "Devices, consumers",
    required: "Code-sign infra",
    stops: "Cannot verify firmware / docs; supply-chain attack risk",
    workaround: "Trust by file hash on a wiki", severity: 4,
    interfaceType: "pki", payloadType: "signature",
    protocol: "Code-sign · X.509 signing · CMS" },
  { id: 101, l1: "Security", l2: "Crypto", l3: "Encrypted transport",
    examples: "TLS records, IPsec tunnels",
    source: "Any sender", destination: "Any receiver",
    required: "TLS / IPsec stack",
    stops: "Data in clear; eavesdropping & MITM risk",
    workaround: "Air-gap network segments", severity: 5,
    interfaceType: "pki", payloadType: "key-material",
    protocol: "TLS 1.2/1.3 · IPsec" },
  { id: 102, l1: "Security", l2: "Audit", l3: "Tamper-evident log",
    examples: "Hash-chained log, append-only audit",
    source: "Critical systems", destination: "Auditors, SIEM",
    required: "Append-only DB or DLT",
    stops: "Cannot prove what happened; audit failure",
    workaround: "Paper logs (slow, weak)", severity: 4,
    interfaceType: "database", payloadType: "event",
    protocol: "Append-only DB · hash-chain · DLT" },

  // ─── Organizational ────────────────────────────────────────────────
  { id: 103, l1: "Organizational", l2: "B2B", l3: "Supplier transactions",
    examples: "EDI 856 ASN, 850 PO, 810 invoice, RosettaNet",
    source: "Suppliers, customers", destination: "ERP",
    required: "B2B gateway (AS2/AS4, X12, EDIFACT)",
    stops: "Supply chain runs on phone / email; shortages spike",
    workaround: "Phone, email, fax PDF", severity: 4,
    interfaceType: "b2b-gateway", payloadType: "transaction",
    protocol: "AS2/AS4 · X12 · EDIFACT · RosettaNet" },
  { id: 104, l1: "Organizational", l2: "B2B", l3: "Forecast / collaboration",
    examples: "VMI, CPFR, S&OP feeds, supplier scorecards",
    source: "ERP, planning", destination: "Suppliers, partners",
    required: "Portal, REST APIs",
    stops: "Suppliers fly blind; bullwhip effect amplifies",
    workaround: "Weekly conference calls", severity: 3,
    interfaceType: "rest-api", payloadType: "structured",
    protocol: "Supplier portal · REST API" },
  { id: 105, l1: "Organizational", l2: "Internal", l3: "Cross-department workflow",
    examples: "Engineering change board, NPI gates, audit responses",
    source: "Department systems", destination: "Other departments",
    required: "Workflow / BPMN engine, ticketing",
    stops: "Hand-offs stall; bottlenecks at every gate",
    workaround: "Email and meetings", severity: 3,
    interfaceType: "rest-api", payloadType: "transaction",
    protocol: "BPMN workflow engine · ticketing" },
  { id: 106, l1: "Organizational", l2: "Regulatory", l3: "Compliance reporting",
    examples: "21 CFR Part 11, IATF, ISO 9001, ISO 14001, GHG Protocol",
    source: "MES, QMS, EHS", destination: "Regulators, auditors",
    required: "Reporting APIs, files, audit logs",
    stops: "Cannot demonstrate compliance; fines, recalls, shutdowns",
    workaround: "Manual evidence compilation per audit", severity: 5,
    interfaceType: "rest-api", payloadType: "document",
    protocol: "Reporting API · evidence bundle · signed PDF" },
  { id: 107, l1: "Organizational", l2: "Customer", l3: "Field feedback loop",
    examples: "Warranty, returns, complaints, NPS",
    source: "Customer service", destination: "Quality, engineering",
    required: "CAPA workflow, REST",
    stops: "Field issues don't reach engineering; repeat failures",
    workaround: "Periodic VOC studies", severity: 3,
    interfaceType: "rest-api", payloadType: "transaction",
    protocol: "REST · CAPA workflow · VOC" },
];

// ─── Aggregate counts ─────────────────────────────────────────────────
export const FLOW_COUNTS = (() => {
  const byL1 = {};
  const bySeverity = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const byInterfaceType = {};
  const byPayloadType = {};
  for (const f of FLOWS) {
    byL1[f.l1] = (byL1[f.l1] || 0) + 1;
    bySeverity[f.severity] = (bySeverity[f.severity] || 0) + 1;
    byInterfaceType[f.interfaceType] = (byInterfaceType[f.interfaceType] || 0) + 1;
    byPayloadType[f.payloadType] = (byPayloadType[f.payloadType] || 0) + 1;
  }
  return { byL1, bySeverity, byInterfaceType, byPayloadType, total: FLOWS.length };
})();

// ─── Convenience selectors ────────────────────────────────────────────
export const flowsByL1       = (l1)   => FLOWS.filter(f => f.l1 === l1);
export const flowsByL2       = (l1, l2) => FLOWS.filter(f => f.l1 === l1 && f.l2 === l2);
export const flowsBySeverity = (sev)  => FLOWS.filter(f => f.severity === sev);
export const flowsByInterface = (ifT) => FLOWS.filter(f => f.interfaceType === ifT);
export const flowsByPayload   = (pT)  => FLOWS.filter(f => f.payloadType === pT);
export const flowById        = (id)   => FLOWS.find(f => f.id === id);

// ─── L3 → flowId index (fuzzy text match for cross-tab linking) ───────
// Used by other tabs to look up taxonomy entries for an arbitrary
// keyword (e.g., a CLM interface's `name` field).
export function findTaxonomyMatches(text) {
  if (!text) return [];
  const needle = String(text).toLowerCase();
  return FLOWS.filter(f => {
    const hay = `${f.l3} ${f.examples} ${f.protocol}`.toLowerCase();
    return hay.includes(needle);
  });
}
